import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  UiBox,
  UiButton,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {SEXUAL_HEALTH_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {Option, Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const initSelectedYesOptions = (arr: Relation[]) => {
  return arr.reduce((res: any, cur: Relation) => {
    if (cur?.key) {
      res[cur?.key] = cur?.value ? cur?.value : '';
      return res;
    }
  }, {});
};

const Questionnaire7Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);

  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[SEXUAL_HEALTH_KEYS.TAKEN_MEDICATION_OR_SIMILAR_CONDITION_BEFORE]?.data,
  );

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(
    i => i.key === SEXUAL_HEALTH_KEYS.TAKEN_MEDICATION_OR_SIMILAR_CONDITION_BEFORE,
  );

  let medicalOptions: Option[] = [];
  if (questionnaire?.options?.length) {
    medicalOptions = questionnaire?.options?.filter(i => i?.value !== 'NONE');
  }

  const {inputs, setInputs, validation, trimSpaces} = useInput({
    selectedOption: currentAnswer?.selectedOption || '',
    relations: currentAnswer?.relations || [],
    selectedYesOptions: currentAnswer?.selectedYesOptions?.length
      ? initSelectedYesOptions(currentAnswer?.selectedYesOptions)
      : {},
  });

  const onClickOption = (answer: {value: string | number; label: string; relations?: Relation[]}) => {
    const {relations, value} = answer;
    setInputs({selectedOption: value, relations: relations || []});
  };

  const isDisabled =
    !inputs.selectedOption ||
    (inputs.selectedOption === 'YES' &&
      !(
        Object.keys(inputs.selectedYesOptions).length > 2 &&
        !Object.keys(inputs.selectedYesOptions).some(i => trimSpaces(inputs.selectedYesOptions[i]).length < 1)
      ));

  const onClickSubmit = async () => {
    dispatch(
      setQuestionnaireAnswer({
        key: SEXUAL_HEALTH_KEYS.TAKEN_MEDICATION_OR_SIMILAR_CONDITION_BEFORE,
        index: questionnaire?.index || '',
        data: {
          ...inputs,
          selectedYesOptions: Object.keys(inputs.selectedYesOptions).map(val => ({
            key: val,
            value: trimSpaces(inputs.selectedYesOptions[val]),
          })),
        },
      }),
    );
    dispatch(setNextQuestionnaire({value: SEXUAL_HEALTH_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY}));
  };

  const renderSymptomInput = () => {
    if (inputs.selectedOption === 'YES') {
      return (
        <UiBox w="100%" mt={spacing.small} className="animate__animated animate__fadeInLeft">
          {inputs?.relations?.map((val: Relation) => {
            if (val?.elementType?.name === 'inputText') {
              return (
                <UiBox w="100%" mt={spacing.small} key={val.key}>
                  <UiText fontSize="md" fontWeight="bold" mt={spacing.tiny}>
                    {val?.questions?.default}
                  </UiText>
                  <UiTextarea
                    mt={spacing.small}
                    bg="white"
                    borderRadius={spacing.tiny}
                    placeholder="Type your response here..."
                    defaultValue={inputs.selectedYesOptions[val?.key]}
                    onChange={({target}) =>
                      setInputs({selectedYesOptions: {...inputs.selectedYesOptions, [val?.key]: target.value}})
                    }
                  />
                </UiBox>
              );
            } else {
              return (
                <UiBox key={val?.questions?.default}>
                  <UiText fontSize="md" fontWeight="bold" mt={spacing.small}>
                    {val?.questions?.default}
                  </UiText>
                  <UiSimpleGrid columns={2} spacing={5} mt={spacing.tiny}>
                    {val?.options?.map(option => (
                      <ScrollToBottomButton
                        key={`${option.label}-${option.value}`}
                        title={option.label}
                        variant={inputs.selectedYesOptions[val.key] === option.value ? 'solid' : 'toggle'}
                        onClick={() =>
                          setInputs({selectedYesOptions: {...inputs.selectedYesOptions, [val?.key]: option.value}})
                        }
                      />
                    ))}
                  </UiSimpleGrid>
                </UiBox>
              );
            }
          })}
        </UiBox>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={isDisabled}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.small} />
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.medium}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => onClickOption(value)}
              />
            ))}
          </UiSimpleGrid>
        </UiBox>
        {renderSymptomInput()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire7Screen;
